import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { IntroNote } from "@/components/IntroNote";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { TrendingDown, TrendingUp, Minus, Activity, ShieldAlert, PoundSterling, Globe } from "lucide-react";

const WINDOWS = [
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "quarter", label: "Quarter" },
];

const METRICS = [
  { key: "posture", label: "Risk posture", icon: Activity },
  { key: "kris", label: "KRIs (RAG)", icon: ShieldAlert },
  { key: "fair", label: "Financial (FAIR £)", icon: PoundSterling },
  { key: "geo", label: "Geopolitics", icon: Globe },
];

const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
const fmtGBP = (v) => new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 }).format(v || 0);

// A directional delta chip. lowerIsBetter = improvement when delta < 0.
const DeltaChip = ({ delta, lowerIsBetter = true, fmt = (v) => v, testid }) => {
  if (delta == null) return null;
  const good = lowerIsBetter ? delta < 0 : delta > 0;
  const flat = delta === 0;
  const Icon = flat ? Minus : delta < 0 ? TrendingDown : TrendingUp;
  const tone = flat ? "text-ek-ink/65" : good ? "text-emerald-600" : "text-red-600";
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 text-sm font-semibold ${tone}`}>
      <Icon size={14} />{delta > 0 ? "+" : ""}{fmt(delta)}
    </span>
  );
};

const SummaryCard = ({ label, value, sub, children, testid }) => (
  <div data-testid={testid} className="rounded-md border border-black/5 bg-white px-5 py-4">
    <p className="text-[0.7rem] font-semibold uppercase tracking-wide text-ek-ink/65">{label}</p>
    <p className="mt-1 text-3xl font-bold text-ek-ink">{value}</p>
    <div className="mt-1 flex items-center gap-2 text-xs text-ek-ink/65">{children}{sub}</div>
  </div>
);

export default function Trends() {
  const [window, setWindow] = useState("quarter");
  const [metric, setMetric] = useState("posture");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/trends?window=${window}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load trends"))
      .finally(() => setLoading(false));
  }, [window]);

  const points = data?.points || [];
  const qtd = data?.quarter_to_date;
  const latest = data?.latest;

  const renderChart = () => {
    if (!points.length) return null;
    if (metric === "posture") {
      return (
        <LineChart data={points} margin={{ top: 10, right: 16, left: 0, bottom: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#00000010" />
          <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: "#6b6b78" }} minTickGap={28} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#6b6b78" }} width={36} />
          <Tooltip labelFormatter={(l) => fmtDate(l)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          <Line type="monotone" dataKey="posture_index" name="Posture index" stroke="#7737F6" strokeWidth={2.5} dot={false} />
        </LineChart>
      );
    }
    if (metric === "kris") {
      return (
        <AreaChart data={points} margin={{ top: 10, right: 16, left: 0, bottom: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#00000010" />
          <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: "#6b6b78" }} minTickGap={28} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b6b78" }} width={28} />
          <Tooltip labelFormatter={(l) => fmtDate(l)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
          <Area type="monotone" dataKey="kri_red" name="Red" stackId="1" stroke="#dc2626" fill="#fca5a5" />
          <Area type="monotone" dataKey="kri_amber" name="Amber" stackId="1" stroke="#d97706" fill="#fcd34d" />
          <Area type="monotone" dataKey="kri_green" name="Green" stackId="1" stroke="#059669" fill="#6ee7b7" />
        </AreaChart>
      );
    }
    if (metric === "fair") {
      return (
        <AreaChart data={points} margin={{ top: 10, right: 16, left: 4, bottom: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#00000010" />
          <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: "#6b6b78" }} minTickGap={28} />
          <YAxis tickFormatter={(v) => `£${fmtGBP(v)}`} tick={{ fontSize: 11, fill: "#6b6b78" }} width={56} />
          <Tooltip formatter={(v) => `£${(v || 0).toLocaleString("en-GB")}`} labelFormatter={(l) => fmtDate(l)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          <Area type="monotone" dataKey="fair_ale_gbp" name="Portfolio ALE" stroke="#7737F6" fill="#ddd1fb" strokeWidth={2} />
        </AreaChart>
      );
    }
    return (
      <LineChart data={points} margin={{ top: 10, right: 16, left: 0, bottom: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#00000010" />
        <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: "#6b6b78" }} minTickGap={28} />
        <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#6b6b78" }} width={36} />
        <Tooltip labelFormatter={(l) => fmtDate(l)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
        <Line type="monotone" dataKey="geo_index" name="Geo exposure index" stroke="#7737F6" strokeWidth={2.5} dot={false} />
      </LineChart>
    );
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-12" data-testid="trends-page">
        <p className="ek-eyebrow">Historical Trends</p>
        <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">Risk trend tracking</h1>
        <p className="mt-3 max-w-2xl text-base text-ek-ink/65">
          Nightly snapshots of your posture, KRIs, financial exposure and geopolitics — see how risk is being
          driven down over the quarter, and what changed in the last week.
        </p>

        <IntroNote icon={Activity} title="What you're looking at" testid="trends-intro" className="mt-6">
          Are we actually getting safer? Nightly snapshots track your posture, KRIs, financial exposure (£) and
          geopolitics over time, with directional deltas. Use it to evidence progress to the board and to spot
          anything quietly drifting the wrong way.
        </IntroNote>

        {/* What changed since last week */}
        {data?.narrative && (
          <div data-testid="trends-narrative" className="mt-6 flex items-start gap-3 rounded-md border border-ek-purple/20 bg-ek-purple/5 px-5 py-4">
            <Activity size={18} className="mt-0.5 shrink-0 text-ek-purple" />
            <div>
              <p className="text-[0.7rem] font-semibold uppercase tracking-wide text-ek-purple">Since last week</p>
              <p className="mt-0.5 text-sm font-medium text-ek-ink">{data.narrative}</p>
            </div>
          </div>
        )}

        {/* Quarter-to-date summary cards */}
        {qtd && latest && (
          <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <SummaryCard testid="summary-posture" label="Posture index" value={latest.posture_index}>
              <DeltaChip testid="delta-posture" delta={qtd.posture.delta} /> <span>this {window}</span>
            </SummaryCard>
            <SummaryCard testid="summary-fair" label="Portfolio ALE" value={`£${fmtGBP(latest.fair_ale_gbp)}`}>
              <DeltaChip testid="delta-fair" delta={qtd.fair_ale_gbp.delta} fmt={(v) => `£${fmtGBP(Math.abs(v))}`} /> <span>this {window}</span>
            </SummaryCard>
            <SummaryCard testid="summary-geo" label="Geo exposure" value={latest.geo_index}>
              <DeltaChip testid="delta-geo" delta={qtd.geo_index.delta} /> <span>this {window}</span>
            </SummaryCard>
            <SummaryCard testid="summary-kri" label="KRIs in red" value={latest.kri_red}>
              <DeltaChip testid="delta-kri" delta={qtd.kri_red.delta} /> <span>this {window}</span>
            </SummaryCard>
          </div>
        )}

        {/* Controls */}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-1 rounded-md border border-black/10 bg-white p-1" data-testid="metric-toggle">
            {METRICS.map((m) => {
              const Icon = m.icon;
              return (
                <button key={m.key} onClick={() => setMetric(m.key)} data-testid={`metric-${m.key}`}
                  className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                    metric === m.key ? "bg-ek-purple text-white" : "text-ek-ink/60 hover:text-ek-purple"
                  }`}>
                  <Icon size={14} /> {m.label}
                </button>
              );
            })}
          </div>
          <div className="flex gap-1 rounded-md border border-black/10 bg-white p-1" data-testid="window-toggle">
            {WINDOWS.map((w) => (
              <button key={w.key} onClick={() => setWindow(w.key)} data-testid={`window-${w.key}`}
                className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                  window === w.key ? "bg-ek-ink text-white" : "text-ek-ink/60 hover:text-ek-ink"
                }`}>
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {/* Chart */}
        <div className="mt-4 rounded-md border border-black/5 bg-white p-5" data-testid="trend-chart">
          {loading ? (
            <p className="py-24 text-center text-sm text-ek-ink/65">Loading trends…</p>
          ) : points.length < 2 ? (
            <p className="py-24 text-center text-sm text-ek-ink/65">Not enough history yet — snapshots accrue nightly.</p>
          ) : (
            <div style={{ width: "100%", height: 360 }}>
              <ResponsiveContainer>{renderChart()}</ResponsiveContainer>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
